// Pomocné funkce nad view-modelem menu — sekce dělené oddělovači, počty listů,
// detekce native a ploché vyhledávání. Sémantika 1:1 s prototypem `menu.jsx`.

import type { MenuItemNode, MenuNode, MenuSection } from './menuTypes';

export interface MenuSectionBlock {
  readonly sep: 'none' | 'space' | 'line';
  readonly nodes: readonly MenuNode[];
}

/** Rozdělí seznam uzlů na bloky oddělené `space`/`line` (sekce nemají název). */
export function splitSections(items: readonly MenuNode[]): MenuSectionBlock[] {
  const out: { sep: 'none' | 'space' | 'line'; nodes: MenuNode[] }[] = [{ sep: 'none', nodes: [] }];
  for (const n of items) {
    if (n.t === 'space' || n.t === 'line') out.push({ sep: n.t, nodes: [] });
    else out[out.length - 1].nodes.push(n);
  }
  return out.filter((s, i) => s.nodes.length > 0 || i > 0);
}

/** Počet listových agend pod uzlem. */
export function leafCount(node: MenuNode): number {
  if (node.t === 'item') return 1;
  if (node.t === 'group') return node.items.reduce((a, n) => a + leafCount(n), 0);
  return 0;
}

/** Vede uzel k nějaké native (okamžité) agendě? */
export function hasNative(node: MenuNode): boolean {
  if (node.t === 'item') return node.origin === 'native';
  if (node.t === 'group') return node.items.some(hasNative);
  return false;
}

/**
 * Viditelnost uzlu během načítání: native agendy hned, legacy až po dotažení
 * (dopředu neznáme, kam legacy položky přibudou — žádné skeletony).
 */
export function nodeVisibleDuringLoad(node: MenuNode, ready: boolean): boolean {
  if (ready) return true;
  return hasNative(node);
}

/** Jeden výsledek fulltextu nad menu — listová agenda + sekce + cesta (breadcrumb). */
export interface MenuSearchHit {
  readonly node: MenuItemNode;
  readonly section: MenuSection;
  /** Názvy rodičovských skupin od sekce k listu (bez sekce samotné). */
  readonly trail: readonly string[];
}

/** Normalizace pro hledání — lowercase + odstranění diakritiky (NFD). */
export function normalizeForSearch(text: string): string {
  return text
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
    .toLowerCase();
}

/**
 * Ploché fulltextové hledání napříč sekcemi (port `walk` z prototypu `menu.jsx`).
 * Matchne listové agendy, jejichž titulek obsahuje `query` (case- i diakritika-insensitive);
 * vrací je v pořadí průchodu se sekcí a breadcrumb cestou. Prázdný dotaz → prázdné pole.
 */
export function flattenMenuForSearch(
  sections: readonly MenuSection[],
  query: string
): MenuSearchHit[] {
  const q = normalizeForSearch(query.trim());
  if (!q) return [];
  const hits: MenuSearchHit[] = [];
  const walk = (nodes: readonly MenuNode[], section: MenuSection, trail: readonly string[]) => {
    for (const n of nodes) {
      if (n.t === 'item') {
        if (normalizeForSearch(n.title).includes(q)) hits.push({ node: n, section, trail });
      } else if (n.t === 'group') {
        walk(n.items, section, [...trail, n.title]);
      }
    }
  };
  for (const section of sections) walk(section.items, section, []);
  return hits;
}
